'use client';
import { useState, useEffect, useRef } from 'react';
import { Dialog } from 'primereact/dialog';
import Link from 'next/link';
import { createSupportTicket } from '@/lib/support/supportTicketService';
import { getWarrantyStatusConfig, formatDate } from '../_helpers';

/* ─── Auth helper ─────────────────────────────────────────────────────────── */
const getAuthUser = () => {
    if (typeof document === 'undefined') return null;
    const match = document.cookie.match(/(?:^|; )authUser=([^;]*)/);
    if (!match) return null;
    try { return JSON.parse(decodeURIComponent(match[1])); } catch { return null; }
};

/* ─── Product preview (white card, light bg) ─────────────────────────────── */
const ProductPreviewCard = ({ item }) => {
    const statusCfg = getWarrantyStatusConfig(item.warranty?.status);
    const isIn      = item.warranty?.status === 'in_warranty';

    return (
        <div
            className="flex align-items-start gap-3 border-round-xl p-3"
            style={{
                background: '#fff',
                border:     '1.5px solid #e5e7eb',
                boxShadow:  '0 2px 8px rgba(0,0,0,0.06)',
            }}
        >
            {/* Icon */}
            <div
                className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                style={{
                    width:      44, height: 44,
                    background: isIn
                        ? 'linear-gradient(135deg, #10b981, #1bb8a7)'
                        : 'linear-gradient(135deg, #ef4444, #f97316)',
                    boxShadow:  isIn
                        ? '0 3px 10px rgba(16,185,129,0.35)'
                        : '0 3px 10px rgba(239,68,68,0.35)',
                }}
            >
                <i className="pi pi-box" style={{ fontSize: '1rem', color: '#fff' }} />
            </div>

            {/* Info */}
            <div className="flex flex-column gap-1 flex-1" style={{ minWidth: 0 }}>
                <p
                    className="m-0 font-bold"
                    style={{
                        fontSize:     '0.9rem',
                        color:        '#1e293b',
                        lineHeight:   1.35,
                        overflow:     'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace:   'nowrap',
                    }}
                >
                    {item.product?.name || 'Unknown Product'}
                </p>

                <div className="flex align-items-center gap-2 flex-wrap">
                    <span
                        className="flex align-items-center gap-1 font-bold border-round-3xl"
                        style={{
                            background: statusCfg.bg,
                            color:      statusCfg.color,
                            border:     `1.5px solid ${statusCfg.border}`,
                            fontSize:   '0.62rem',
                            padding:    '0.18rem 0.55rem',
                        }}
                    >
                        <i className={statusCfg.icon} style={{ fontSize: '0.58rem' }} />
                        {statusCfg.label}
                    </span>
                    {item.product?.quantity && (
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            Qty: {item.product.quantity}
                        </span>
                    )}
                    {item.product?.totalAmount && (
                        <span style={{ fontSize: '0.7rem', color: '#64748b' }}>
                            ₹{item.product.totalAmount.toLocaleString('en-IN')}
                        </span>
                    )}
                </div>

                {/* Meta */}
                <div className="flex align-items-center gap-3 flex-wrap mt-1">
                    {item.invoiceNumber && (
                        <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                            Invoice: {item.invoiceNumber}
                        </span>
                    )}
                    {item.invoiceDate && (
                        <span style={{ fontSize: '0.66rem', color: '#94a3b8' }}>
                            {formatDate(item.invoiceDate)}
                        </span>
                    )}
                    {item.warranty?.warrantyExpiry && (
                        <span style={{ fontSize: '0.66rem', color: isIn ? '#059669' : '#dc2626', fontWeight: 600 }}>
                            {isIn ? 'Expires: ' : 'Expired: '}
                            {formatDate(item.warranty.warrantyExpiry)}
                        </span>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ─── Field label ─────────────────────────────────────────────────────────── */
const FieldLabel = ({ label, required }) => (
    <label
        style={{
            display:       'block',
            fontSize:      '0.72rem',
            fontWeight:    700,
            color:         '#64748b',
            textTransform: 'uppercase',
            letterSpacing: '0.05em',
            marginBottom:  '0.4rem',
        }}
    >
        {label}
        {required && <span style={{ color: '#ef4444', marginLeft: '0.2rem' }}>*</span>}
    </label>
);

/* ─── Input style ─────────────────────────────────────────────────────────── */
const inputStyle = (hasError) => ({
    width:        '100%',
    background:   'var(--surface-card)',
    border:       `1.5px solid ${hasError ? '#ef4444' : 'var(--surface-border)'}`,
    borderRadius: '0.65rem',
    padding:      '0.6rem 0.85rem',
    fontSize:     '0.88rem',
    color:        'var(--text-color)',
    outline:      'none',
    boxSizing:    'border-box',
    transition:   'border-color 0.2s',
});

/* ─── File attachment chip ────────────────────────────────────────────────── */
const AttachmentChip = ({ file, onRemove }) => (
    <div
        className="flex align-items-center gap-2 border-round-lg px-2 py-1"
        style={{
            background: '#f1f5f9',
            border:     '1px solid #e2e8f0',
            fontSize:   '0.72rem',
            color:      '#475569',
        }}
    >
        <i className="pi pi-paperclip" style={{ fontSize: '0.65rem', color: '#94a3b8' }} />
        <span style={{ maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
        </span>
        <button
            onClick={onRemove}
            style={{
                background: 'transparent', border: 'none', cursor: 'pointer',
                color: '#ef4444', padding: 0, lineHeight: 1,
            }}
        >
            <i className="pi pi-times" style={{ fontSize: '0.6rem' }} />
        </button>
    </div>
);

/* ─── Ticket number copy pill ─────────────────────────────────────────────── */
const TicketNumberCopy = ({ ticketNumber }) => {
    const [copied, setCopied] = useState(false);
    const doCopy = () => {
        navigator.clipboard.writeText(ticketNumber).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };
    return (
        <div
            className="flex align-items-center gap-2 border-round-xl px-4 py-3"
            style={{
                background: 'linear-gradient(135deg, rgba(27,184,167,0.08), rgba(99,102,241,0.06))',
                border:     '1.5px solid rgba(27,184,167,0.25)',
                width:      '100%',
                boxSizing:  'border-box',
            }}
        >
            <i className="pi pi-ticket" style={{ fontSize: '1rem', color: '#1bb8a7', flexShrink: 0 }} />
            <div className="flex flex-column flex-1" style={{ minWidth: 0 }}>
                <span style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                    Ticket Number
                </span>
                <span className="font-bold" style={{ fontSize: '1rem', color: '#1bb8a7', letterSpacing: '0.03em' }}>
                    {ticketNumber}
                </span>
            </div>
            <button
                type="button"
                onClick={doCopy}
                title="Copy ticket number"
                style={{
                    display:      'flex',
                    alignItems:   'center',
                    gap:          '0.3rem',
                    padding:      '0.4rem 0.75rem',
                    borderRadius: '0.5rem',
                    border:       '1.5px solid rgba(27,184,167,0.3)',
                    background:   copied ? 'rgba(27,184,167,0.18)' : 'rgba(27,184,167,0.08)',
                    color:        copied ? '#1bb8a7' : '#64748b',
                    fontSize:     '0.72rem',
                    fontWeight:   700,
                    cursor:       'pointer',
                    transition:   'all 0.15s',
                    whiteSpace:   'nowrap',
                    flexShrink:   0,
                }}
                onMouseEnter={e => { if (!copied) { e.currentTarget.style.background = 'rgba(27,184,167,0.14)'; e.currentTarget.style.color = '#1bb8a7'; } }}
                onMouseLeave={e => { if (!copied) { e.currentTarget.style.background = 'rgba(27,184,167,0.08)'; e.currentTarget.style.color = '#64748b'; } }}
            >
                <i className={copied ? 'pi pi-check' : 'pi pi-copy'} style={{ fontSize: '0.72rem' }} />
                {copied ? 'Copied!' : 'Copy'}
            </button>
        </div>
    );
};

/* ─── Confirm-before-submit screen ───────────────────────────────────────── */
const ConfirmRow = ({ icon, label, value }) => {
    if (!value) return null;
    return (
        <div className="flex align-items-start gap-2">
            <i className={icon} style={{ fontSize: '0.8rem', color: '#94a3b8', marginTop: '0.2rem', flexShrink: 0 }} />
            <div style={{ minWidth: 0 }}>
                <p className="m-0" style={{ fontSize: '0.62rem', fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    {label}
                </p>
                <p className="m-0" style={{ fontSize: '0.85rem', fontWeight: 600, color: '#1e293b', whiteSpace: 'pre-line', lineHeight: 1.45, wordBreak: 'break-word' }}>
                    {value}
                </p>
            </div>
        </div>
    );
};

const ConfirmScreen = ({ form, item, pickupAddress, attachmentCount, submitting, apiErr, onBack, onConfirm }) => (
    <div className="flex flex-column gap-4">
        <div
            className="flex align-items-center gap-2 border-round-lg px-3 py-2"
            style={{ background: '#eff6ff', border: '1px solid #bfdbfe' }}
        >
            <i className="pi pi-info-circle" style={{ color: '#2563eb', fontSize: '0.9rem' }} />
            <span style={{ fontSize: '0.8rem', color: '#1e40af', fontWeight: 600 }}>
                Please review your details before raising the ticket
            </span>
        </div>

        <div className="flex flex-column gap-3 border-round-xl p-3" style={{ background: '#fff', border: '1.5px solid #e5e7eb' }}>
            <ConfirmRow icon="pi pi-box"       label="Product"          value={item?.product?.name} />
            <ConfirmRow icon="pi pi-user"      label="Full Name"        value={form.name} />
            <ConfirmRow icon="pi pi-envelope"  label="Email"            value={form.email} />
            <ConfirmRow icon="pi pi-phone"     label="Phone"            value={form.mobile} />
            <ConfirmRow icon="pi pi-map-marker" label="Pickup Address"  value={pickupAddress} />
            <ConfirmRow icon="pi pi-align-left" label="Issue Description" value={form.description} />
            {attachmentCount > 0 && (
                <ConfirmRow icon="pi pi-paperclip" label="Attachments" value={`${attachmentCount} file(s) attached`} />
            )}
        </div>

        {apiErr && (
            <div
                className="flex align-items-center gap-2 border-round-lg px-3 py-2"
                style={{ background: '#fef2f2', border: '1px solid #fecaca', color: '#dc2626', fontSize: '0.82rem' }}
            >
                <i className="pi pi-exclamation-circle" style={{ fontSize: '0.88rem', flexShrink: 0 }} />
                <span>{apiErr}</span>
            </div>
        )}

        <div className="flex gap-3">
            <button
                onClick={onBack}
                disabled={submitting}
                style={{
                    flex: '1 1 0', background: '#fff', border: '1.5px solid #e2e8f0',
                    borderRadius: '0.8rem', padding: '0.8rem 1rem', fontSize: '0.88rem',
                    fontWeight: 700, color: '#475569', cursor: submitting ? 'not-allowed' : 'pointer',
                }}
            >
                <i className="pi pi-arrow-left" style={{ marginRight: '0.4rem', fontSize: '0.8rem' }} />
                Edit Details
            </button>
            <button
                onClick={onConfirm}
                disabled={submitting}
                style={{
                    flex: '1 1 0',
                    background:     submitting ? '#e2e8f0' : 'var(--primary-color)',
                    border:         'none',
                    borderRadius:   '0.8rem',
                    padding:        '0.8rem 1rem',
                    fontSize:       '0.88rem',
                    fontWeight:     700,
                    color:          submitting ? '#94a3b8' : '#fff',
                    cursor:         submitting ? 'not-allowed' : 'pointer',
                    display:        'flex',
                    alignItems:     'center',
                    justifyContent: 'center',
                    gap:            '0.5rem',
                    boxShadow:      submitting ? 'none' : '0 4px 18px rgba(11,49,99,0.3)',
                }}
            >
                {submitting ? (
                    <>
                        <i className="pi pi-spin pi-spinner" style={{ fontSize: '0.9rem' }} />
                        Raising ticket…
                    </>
                ) : (
                    <>
                        <i className="pi pi-check" style={{ fontSize: '0.9rem' }} />
                        Confirm &amp; Raise Ticket
                    </>
                )}
            </button>
        </div>
    </div>
);

/* ─── Success screen (dark bg, white text) ───────────────────────────────── */
const SuccessScreen = ({ data, onDone }) => (
    <div
        className="flex flex-column align-items-center gap-4 text-center px-4 py-5"
        style={{ background: '#ffffff' }}
    >
        {/* Icon */}
        <div
            className="flex align-items-center justify-content-center border-circle"
            style={{
                width:      72, height: 72,
                background: 'linear-gradient(135deg, #1bb8a7, #6366f1)',
                boxShadow:  '0 8px 28px rgba(27,184,167,0.4)',
            }}
        >
            <i className="pi pi-check" style={{ fontSize: '1.8rem', color: '#fff' }} />
        </div>

        {/* Title */}
        <div>
            <h3 className="m-0" style={{ fontSize: '1.25rem', fontWeight: 800, color: '#0b3163' }}>
                Ticket Raised Successfully!
            </h3>
            {data?.name && (
                <p className="m-0 mt-1" style={{ fontSize: '0.85rem', color: '#64748b' }}>
                    Thank you, <strong style={{ color: '#0b3163' }}>{data.name}</strong>
                </p>
            )}
        </div>

        {/* Ticket number pill */}
        {data?.ticketNumber && (
            <TicketNumberCopy ticketNumber={data.ticketNumber} />
        )}

        {/* Message block */}
        <div
            className="flex flex-column gap-2 border-round-xl px-4 py-3 text-left"
            style={{
                background: '#f8faff',
                border:     '1px solid rgba(255,255,255,0.1)',
                width:      '100%',
                boxSizing:  'border-box',
            }}
        >
            <div className="flex align-items-start gap-2">
                <i className="pi pi-send" style={{ fontSize: '0.85rem', color: '#34d399', marginTop: '0.15rem', flexShrink: 0 }} />
                <p className="m-0" style={{ fontSize: '0.84rem', color: '#334155', lineHeight: 1.65 }}>
                    Our team will review your repair request and send a detailed quote to your email.
                    We will check and revert to you soon.
                </p>
            </div>
            {data?.email && (
                <div className="flex align-items-center gap-2 mt-1">
                    <i className="pi pi-envelope" style={{ fontSize: '0.75rem', color: '#34d399', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                        Response will be sent to <strong style={{ color: '#0b3163' }}>{data.email}</strong>
                    </span>
                </div>
            )}
            {data?.mobile && (
                <div className="flex align-items-center gap-2">
                    <i className="pi pi-phone" style={{ fontSize: '0.75rem', color: '#34d399', flexShrink: 0 }} />
                    <span style={{ fontSize: '0.78rem', color: '#64748b' }}>
                        We&apos;ll also contact you on <strong style={{ color: '#0b3163' }}>{data.mobile}</strong>
                    </span>
                </div>
            )}
        </div>

        {/* Actions */}
        <div className="flex gap-3 flex-wrap justify-content-center" style={{ width: '100%' }}>
            {data?.ticketNumber && (
                <Link
                    href={`/track-status?q=${encodeURIComponent(data.ticketNumber)}`}
                    className="no-underline"
                >
                    <div
                        className="flex align-items-center gap-2 border-round-lg px-4 py-2"
                        style={{
                            background: 'linear-gradient(135deg, rgba(27,184,167,0.25), rgba(99,102,241,0.2))',
                            border:     '1.5px solid rgba(27,184,167,0.45)',
                            color:      '#34d399',
                            fontWeight: 700,
                            fontSize:   '0.85rem',
                            cursor:     'pointer',
                            transition: 'all 0.15s',
                        }}
                        onMouseEnter={e => { e.currentTarget.style.background = 'rgba(27,184,167,0.35)'; }}
                        onMouseLeave={e => { e.currentTarget.style.background = 'linear-gradient(135deg, rgba(27,184,167,0.25), rgba(99,102,241,0.2))'; }}
                    >
                        <i className="pi pi-search" style={{ fontSize: '0.78rem' }} />
                        Track Repair Status
                        <i className="pi pi-arrow-right" style={{ fontSize: '0.62rem' }} />
                    </div>
                </Link>
            )}
            <button
                onClick={onDone}
                style={{
                    background:   'rgba(11,49,99,0.06)',
                    border:       '1.5px solid rgba(11,49,99,0.2)',
                    borderRadius: '0.65rem',
                    padding:      '0.5rem 1.5rem',
                    fontSize:     '0.85rem',
                    fontWeight:   700,
                    color:        '#475569',
                    cursor:       'pointer',
                    transition:   'all 0.15s',
                }}
                onMouseEnter={e => { e.currentTarget.style.background = 'rgba(11,49,99,0.12)'; e.currentTarget.style.color = '#0b3163'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(11,49,99,0.06)'; e.currentTarget.style.color = '#475569'; }}
            >
                Done
            </button>
        </div>
    </div>
);

/* ─── Main Modal ──────────────────────────────────────────────────────────── */
export const RepairQuoteModal = ({ visible, onHide, item, invoiceFile }) => {
    const fileInputRef = useRef(null);

    const [form, setForm]           = useState({ name: '', email: '', mobile: '', description: '' });
    const [errors, setErrors]       = useState({});
    const [attachments, setAttach]  = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [success, setSuccess]     = useState(null);
    const [apiErr, setApiErr]       = useState('');
    const [showConfirm, setShowConfirm] = useState(false);

    // Pickup address — auto-extracted from the uploaded invoice PDF (shipping
    // or, for invoices without one, the billing address). item.shipping.address
    // is already broken into two lines by the backend: "<area/street>" then
    // "<CITY, STATE - PINCODE>". Shared by the confirm screen and submission.
    const pickupAddress = item?.shipping?.address
        ? [item.shipping.name, item.shipping.address].filter(Boolean).join('\n')
        : '';

    /* Pre-fill from auth cookie */
    useEffect(() => {
        if (!visible) return;
        const u = getAuthUser();
        if (!u) return;
        const name = u.name || [u.firstName, u.lastName].filter(Boolean).join(' ') || '';
        setForm(f => ({
            ...f,
            name:   f.name   || name,
            email:  f.email  || u.email  || '',
            mobile: f.mobile || u.mobile || u.phone || u.contact || '',
        }));
    }, [visible]);

    /* Reset on close */
    const handleHide = () => {
        setSuccess(null);
        setApiErr('');
        setErrors({});
        setAttach([]);
        setShowConfirm(false);
        setForm({ name: '', email: '', mobile: '', description: '' });
        onHide();
    };

    const validate = () => {
        const e = {};
        if (!form.name.trim())        e.name        = 'Name is required';
        if (!form.email.trim())       e.email       = 'Email is required';
        if (!form.mobile.trim())      e.mobile      = 'Phone is required';
        if (!form.description.trim()) e.description = 'Please describe the issue';
        setErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleFileAdd = (e) => {
        const files = Array.from(e.target.files || []);
        setAttach(prev => [...prev, ...files].slice(0, 5));
        e.target.value = '';
    };

    /* Validate, then show the confirmation screen — the actual API call only
       happens after the user confirms the details on that screen. */
    const handleSubmit = () => {
        if (!validate()) return;
        setApiErr('');
        setShowConfirm(true);
    };

    const submitTicket = async () => {
        setSubmitting(true);
        setApiErr('');

        const productInfo = item
            ? `${item.product?.name || 'Unknown Product'} | Invoice: ${item.invoiceNumber || '—'} | Date: ${formatDate(item.invoiceDate)} | Warranty: ${item.warranty?.status || '—'}`
            : '';

        const allAttachments = invoiceFile
            ? [invoiceFile, ...attachments]
            : attachments;

        const res = await createSupportTicket({
            name:        form.name.trim(),
            email:       form.email.trim(),
            mobile:      form.mobile.trim(),
            queryType:   'Warranty Claim',
            category:    'REPAIR_QUOTE',
            product:     productInfo,
            address:     pickupAddress,
            area:        item?.shipping?.area || '',
            city:        item?.shipping?.city || '',
            state:       item?.shipping?.state || '',
            pincode:     item?.shipping?.pincode || '',
            description: form.description.trim(),
            message:     form.description.trim(),
            attachments: allAttachments,
        });

        setSubmitting(false);

        if (!res.success) {
            setApiErr(res.message || 'Something went wrong. Please try again.');
            return;
        }

        setSuccess({
            name:         form.name.trim(),
            email:        form.email.trim(),
            mobile:       form.mobile.trim(),
            ticketNumber: res.data?.ticketNumber || res.data?.ticket?.ticketNumber || null,
        });
    };

    if (!item) return null;

    return (
        <Dialog
            visible={visible}
            onHide={handleHide}
            modal
            dismissableMask={!submitting}
            style={{ width: '92vw', maxWidth: '560px' }}
            contentStyle={{ padding: 0 }}
            headerStyle={{ display: 'none' }}
            pt={{
                root:    { style: { borderRadius: '1.25rem', overflow: 'hidden', border: 'none' } },
                content: { style: { borderRadius: '1.25rem', padding: 0 } },
                mask:    { style: { backdropFilter: 'blur(6px)', background: 'rgba(0,0,0,0.55)' } },
            }}
        >
            {/* ── Modal Header (light surface) ─────────────────────────── */}
            <div
                style={{
                    background: 'var(--surface-card, #fff)',
                    borderBottom: '1.5px solid var(--surface-border, #e5e7eb)',
                    padding:    '1.25rem 1.5rem 1rem',
                    position:   'relative',
                }}
            >
                {/* Close button */}
                <button
                    onClick={handleHide}
                    style={{
                        position:       'absolute', top: '1rem', right: '1rem',
                        width:          32, height: 32, borderRadius: '50%',
                        border:         '1.5px solid #e2e8f0',
                        background:     '#f1f5f9',
                        color:          '#64748b',
                        display:        'flex', alignItems: 'center', justifyContent: 'center',
                        cursor:         'pointer',
                        transition:     'all 0.2s',
                    }}
                    onMouseEnter={e => { e.currentTarget.style.background = '#e2e8f0'; e.currentTarget.style.color = '#334155'; }}
                    onMouseLeave={e => { e.currentTarget.style.background = '#f1f5f9'; e.currentTarget.style.color = '#64748b'; }}
                >
                    <i className="pi pi-times" style={{ fontSize: '0.7rem' }} />
                </button>

                {/* Title row */}
                <div className="flex align-items-center gap-3 mb-3">
                    <div
                        className="flex align-items-center justify-content-center border-circle flex-shrink-0"
                        style={{
                            width:      40, height: 40,
                            background: 'linear-gradient(135deg, #1bb8a7, #6366f1)',
                            boxShadow:  '0 3px 12px rgba(27,184,167,0.35)',
                        }}
                    >
                        <i className="pi pi-wrench" style={{ fontSize: '1rem', color: '#fff' }} />
                    </div>
                    <div>
                        <p className="m-0 font-bold" style={{ fontSize: '1rem', color: 'var(--text-color, #0b3163)', lineHeight: 1.2 }}>
                            Get Repair Quote
                        </p>
                        <p className="m-0 mt-1" style={{ color: 'var(--text-color-secondary, #64748b)', fontSize: '0.76rem' }}>
                            Raise a support ticket — we'll send you a quote within 24 hrs
                        </p>
                    </div>
                </div>

                {/* Product preview (white card) */}
                <ProductPreviewCard item={item} />
            </div>

            {/* ── Body ─────────────────────────────────────────────────── */}
            <div
                style={{
                    background: 'var(--surface-ground, #f8fafc)',
                    padding:    success ? 0 : '1.5rem',
                    overflowY:  'auto',
                    maxHeight:  '65vh',
                }}
            >
                {success ? (
                    <SuccessScreen data={success} onDone={handleHide} />
                ) : showConfirm ? (
                    <ConfirmScreen
                        form={form}
                        item={item}
                        pickupAddress={pickupAddress}
                        attachmentCount={(invoiceFile ? 1 : 0) + attachments.length}
                        submitting={submitting}
                        apiErr={apiErr}
                        onBack={() => setShowConfirm(false)}
                        onConfirm={submitTicket}
                    />
                ) : (
                    <div className="flex flex-column gap-4">

                        {/* Issue description */}
                        <div>
                            <FieldLabel label="Describe the Issue" required />
                            <textarea
                                rows={4}
                                placeholder="E.g. Device stopped working, screen flickering, won't turn on…"
                                value={form.description}
                                onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
                                style={{
                                    ...inputStyle(!!errors.description),
                                    resize:     'vertical',
                                    minHeight:  '90px',
                                    fontFamily: 'inherit',
                                }}
                                onFocus={e  => { e.target.style.borderColor = '#1bb8a7'; }}
                                onBlur={e   => { e.target.style.borderColor = errors.description ? '#ef4444' : 'var(--surface-border)'; }}
                            />
                            {errors.description && (
                                <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#ef4444' }}>
                                    {errors.description}
                                </p>
                            )}
                        </div>

                        {/* Contact info */}
                        <div>
                            <p
                                style={{
                                    margin:        '0 0 0.75rem',
                                    fontSize:      '0.7rem',
                                    fontWeight:    700,
                                    color:         '#94a3b8',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.06em',
                                }}
                            >
                                Your Contact Details
                            </p>

                            {/* Name */}
                            <div className="mb-3">
                                <FieldLabel label="Full Name" required />
                                <input
                                    type="text"
                                    placeholder="John Doe"
                                    value={form.name}
                                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                                    style={inputStyle(!!errors.name)}
                                    onFocus={e => { e.target.style.borderColor = '#1bb8a7'; }}
                                    onBlur={e  => { e.target.style.borderColor = errors.name ? '#ef4444' : 'var(--surface-border)'; }}
                                />
                                {errors.name && (
                                    <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#ef4444' }}>{errors.name}</p>
                                )}
                            </div>

                            {/* Email + Phone row */}
                            <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
                                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                    <FieldLabel label="Email" required />
                                    <input
                                        type="email"
                                        placeholder="you@example.com"
                                        value={form.email}
                                        onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                                        style={inputStyle(!!errors.email)}
                                        onFocus={e => { e.target.style.borderColor = '#1bb8a7'; }}
                                        onBlur={e  => { e.target.style.borderColor = errors.email ? '#ef4444' : 'var(--surface-border)'; }}
                                    />
                                    {errors.email && (
                                        <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#ef4444' }}>{errors.email}</p>
                                    )}
                                </div>
                                <div style={{ flex: '1 1 160px', minWidth: 0 }}>
                                    <FieldLabel label="Phone" required />
                                    <input
                                        type="tel"
                                        placeholder="+91 98765 43210"
                                        value={form.mobile}
                                        onChange={e => setForm(f => ({ ...f, mobile: e.target.value }))}
                                        style={inputStyle(!!errors.mobile)}
                                        onFocus={e => { e.target.style.borderColor = '#1bb8a7'; }}
                                        onBlur={e  => { e.target.style.borderColor = errors.mobile ? '#ef4444' : 'var(--surface-border)'; }}
                                    />
                                    {errors.mobile && (
                                        <p style={{ margin: '0.3rem 0 0', fontSize: '0.72rem', color: '#ef4444' }}>{errors.mobile}</p>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Attachments */}
                        <div>
                            <FieldLabel label="Attach Photos / Documents (optional, max 5)" />
                            {attachments.length > 0 && (
                                <div className="flex flex-wrap gap-2 mb-2">
                                    {attachments.map((f, i) => (
                                        <AttachmentChip
                                            key={i}
                                            file={f}
                                            onRemove={() => setAttach(prev => prev.filter((_, j) => j !== i))}
                                        />
                                    ))}
                                </div>
                            )}
                            {attachments.length < 5 && (
                                <>
                                    <button
                                        type="button"
                                        onClick={() => fileInputRef.current?.click()}
                                        style={{
                                            background:   'transparent',
                                            border:       '1.5px dashed #cbd5e1',
                                            borderRadius: '0.65rem',
                                            padding:      '0.5rem 1rem',
                                            fontSize:     '0.78rem',
                                            color:        '#94a3b8',
                                            cursor:       'pointer',
                                            display:      'flex',
                                            alignItems:   'center',
                                            gap:          '0.4rem',
                                            transition:   'all 0.2s',
                                        }}
                                        onMouseEnter={e => { e.currentTarget.style.borderColor = '#1bb8a7'; e.currentTarget.style.color = '#1bb8a7'; }}
                                        onMouseLeave={e => { e.currentTarget.style.borderColor = '#cbd5e1'; e.currentTarget.style.color = '#94a3b8'; }}
                                    >
                                        <i className="pi pi-paperclip" style={{ fontSize: '0.72rem' }} />
                                        Add attachment
                                    </button>
                                    <input
                                        ref={fileInputRef}
                                        type="file"
                                        multiple
                                        accept="image/*,application/pdf"
                                        style={{ display: 'none' }}
                                        onChange={handleFileAdd}
                                    />
                                </>
                            )}
                        </div>

                        {/* Continue to review */}
                        <button
                            onClick={handleSubmit}
                            style={{
                                background:     'var(--primary-color)',
                                border:         'none',
                                borderRadius:   '0.8rem',
                                padding:        '0.8rem 1.5rem',
                                fontSize:       '0.92rem',
                                fontWeight:     700,
                                color:          '#fff',
                                cursor:         'pointer',
                                display:        'flex',
                                alignItems:     'center',
                                justifyContent: 'center',
                                gap:            '0.5rem',
                                boxShadow:      '0 4px 18px rgba(11,49,99,0.3)',
                                transition:     'all 0.25s',
                                width:          '100%',
                            }}
                            onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-1px)'; }}
                            onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; }}
                        >
                            <i className="pi pi-arrow-right" style={{ fontSize: '0.9rem' }} />
                            Review &amp; Submit
                        </button>
                    </div>
                )}
            </div>
        </Dialog>
    );
};
